// import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// import { type IPlanType } from "./types";
// import { logger } from "@/core";

// const initialState: IPlanType = {
//   planType: "studentPlan",
//   pendingUpgrade: null,
//   checkoutSource: null,
// };

// const planTypeSlice = createSlice({
//   name: "planType",
//   initialState,
//   reducers: {
//     setPlanType: (state, action: PayloadAction<IPlanType>) => {
//       state.planType = action.payload.planType;
//       // state.planPrice = action.payload.planPrice;
//       // state.discountedPlanPrice = action.payload.discountedPlanPrice;
//     },
//     resetPlanType: (state) => {
//       state.planType = null;
//       // state.planPrice = null;
//       // state.discountedPlanPrice = null;
//     },
//     // NEW: For new purchase flow from verifyToken
//     initiateNewPurchase: (
//       state,
//       action: PayloadAction<{ planType: "studentPlan" | "teacherPlan" }>,
//     ) => {
//       logger.info("action", action);
//       state.checkoutSource = "verifyToken";
//       state.pendingUpgrade = null;
//       // Don't change actual planType yet
//     },
//     initiatePlanUpgrade: (
//       state,
//       action: PayloadAction<{ targetPlan: "teacherPlan" }>,
//     ) => {
//       state.pendingUpgrade = action.payload.targetPlan;
//       state.checkoutSource = "courses";
//     },
//     // NEW: Complete checkout (call this after successful payment)
//     completeTeacherPlanPurchased: (
//       state,
//       action: PayloadAction<{ planType?: "studentPlan" | "teacherPlan" }>,
//     ) => {
//       // Use provided planType, fallback to pendingUpgrade, or default to teacherPlan
//       state.planType =
//         action.payload.planType || state.pendingUpgrade || "teacherPlan";

//       // Clear checkout state
//       state.pendingUpgrade = null;
//       state.checkoutSource = null;
//     },

//     // NEW: Cancel checkout (user cancels payment)
//     cancelCheckout: (state) => {
//       // Keep original planType, just clear checkout state
//       state.pendingUpgrade = null;
//       state.checkoutSource = null;
//     },
//   },
// });

// // Export actions
// export const {
//   setPlanType,
//   resetPlanType,
//   initiateNewPurchase,
//   initiatePlanUpgrade,
//   completeTeacherPlanPurchased,
//   cancelCheckout,
// } = planTypeSlice.actions;
// export default planTypeSlice.reducer;
/**
 * planType Slice — @/store/slices/planType
 *
 * Semantic contract (important):
 *   planType        → the plan the user has SELECTED to buy / last
 *                     finalized client-side. The plan the user OWNS is
 *                     auth.user (server truth) — guards and entitlement
 *                     checks read ownership from auth, never from here.
 *   pendingUpgrade  → target plan while an upgrade checkout is active.
 *   checkoutSource  → non-null means "a checkout was deliberately
 *                     started this session". This is the intent lock the
 *                     funnel guard reads for PAID users. Unpaid users
 *                     pass the guard regardless — for them this field is
 *                     just useful metadata (analytics, flow origin).
 */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { IPlanType } from "./types";

/** Consider moving to ./types — single source for the plan union. */
export type TPlan = "studentPlan" | "teacherPlan";

const initialState: IPlanType = {
  planType: "studentPlan",
  pendingUpgrade: null,
  checkoutSource: null,
};

const planTypeSlice = createSlice({
  name: "planType",
  initialState,
  reducers: {
    /**
     * FIXED: payload narrowed from PayloadAction<IPlanType> (the whole
     * state shape) to just what's read — callers can no longer pass
     * pendingUpgrade/checkoutSource that get silently ignored.
     */
    setPlanType: (state, action: PayloadAction<{ planType: TPlan }>) => {
      state.planType = action.payload.planType;
    },

    resetPlanType: (state) => {
      state.planType = null;
    },

    /**
     * New-purchase flow: verifyToken → funnel.
     * FIXED: the payload was accepted but never stored (dead parameter);
     * it now records the selected plan so payment pages know what to
     * charge. FIXED: logger removed — reducers must be pure, and side
     * effects here re-fire on DevTools replay. Log at the dispatch site.
     */
    initiateNewPurchase: (
      state,
      action: PayloadAction<{ planType: TPlan }>,
    ) => {
      state.planType = action.payload.planType;
      state.pendingUpgrade = null;
      state.checkoutSource = "verifyToken";
    },

    /**
     * Upgrade flow: courses → funnel.
     * Dispatching this is what unlocks the funnel for a PAID user.
     */
    initiatePlanUpgrade: (
      state,
      action: PayloadAction<{ targetPlan: "teacherPlan" }>,
    ) => {
      state.pendingUpgrade = action.payload.targetPlan;
      state.checkoutSource = "courses";
    },

    /**
     * After successful payment — dispatched by BOTH success paths
     * (card onSuccess handler and the ElanPay success page).
     * Clearing checkoutSource here is what re-engages the lock, making
     * the back button harmless after purchase.
     */
    completeTeacherPlanPurchased: (
      state,
      action: PayloadAction<{ planType?: TPlan }>,
    ) => {
      state.planType =
        action.payload.planType || state.pendingUpgrade || "teacherPlan";
      state.pendingUpgrade = null;
      state.checkoutSource = null;
    },

    /**
     * User abandons checkout. Dispatch on CoursesPage mount so any
     * stale intent dies the moment the user is back at safe harbor.
     */
    cancelCheckout: (state) => {
      state.pendingUpgrade = null;
      state.checkoutSource = null;
    },
  },
});

export const {
  setPlanType,
  resetPlanType,
  initiateNewPurchase,
  initiatePlanUpgrade,
  completeTeacherPlanPurchased,
  cancelCheckout,
} = planTypeSlice.actions;

export default planTypeSlice.reducer;
